import { getUserPublicCalculateUserGet } from "../client";
import type { GetUserPublicCalculateUserGetResponse } from "../client/types.gen";
import { getApiBase, getCommonHeaders } from "../utils/net/apiRequestHeaders";

/**
 * Frontend user model. Field names are CANONICAL — identical to the backend
 * `UserData` response schema (generated `GetUserPublicCalculateUserGetResponse`).
 * No naming translation: {@link getUserData} only applies defaults / light type
 * normalization (user_id → string, exp_to_levelup → integer).
 */
export interface UserData {
  level: number;
  exp_current: number;
  exp_to_levelup: number;
  currency_balance: number;
  energy_seconds: number; // залишок енергії в секундах
  user_id: string;
  total_taps: number;
  daily_taps: number; // кількість тапів сьогодні
  is_admin?: boolean; // чи є користувач адміном
  vip_until?: string | null; // ISO datetime string або null
  photo_url?: string | null; // URL фото профілю користувача
  first_name?: string | null; // Ім'я користувача
  last_name?: string | null; // Прізвище користувача
  username?: string | null; // Username користувача
  level_cap?: number | null; // Gate level user is capped at
  next_gate_price?: number | null; // Stars price for the next level gate
  level_capped?: boolean; // True when XP is capped at a gate (lock should show)
  exp_booster_until?: string | null; // ISO datetime string when booster expires
  exp_booster_multiplier?: number; // Active XP multiplier (1 = no booster)
  balance_ton?: number; // TON balance from marketplace
  balance_usdt?: number; // USDT balance from marketplace
  currency_multiplier?: number; // Authoritative server pearl-mining multiplier (1 + VIP 0.2 + mayor 0.2)
  is_mayor?: boolean; // True while the user is the sitting mayor (+20% mining perk active)
  notifications_warmup_granted?: boolean; // The bot has delivered a message to the player (allowed + verified)
  comeback_offer?: boolean; // Back after a long absence: may replay the tutorial (and get its reward again)
  comeback_vip_days?: number; // This return brought a VIP gift of this many days
}

/**
 * Applies defaults to the raw calculate-user response. Names already match the
 * backend, so this is a normalizer (defaults + light type coercion), not a mapper.
 */
const normalizeUserData = (apiData: GetUserPublicCalculateUserGetResponse): UserData => ({
  level: apiData.level ?? 1,
  exp_current: apiData.exp_current ?? 0,
  exp_to_levelup: Math.floor(apiData.exp_to_levelup ?? 10),
  currency_balance: apiData.currency_balance ?? 0,
  energy_seconds: apiData.energy_seconds ?? 0,
  user_id: String(apiData.user_id ?? ""),
  total_taps: apiData.total_taps ?? 0,
  daily_taps: apiData.daily_taps ?? 0,
  is_admin: apiData.is_admin ?? false,
  vip_until: apiData.vip_until ?? null,
  photo_url: apiData.photo_url ?? null,
  first_name: apiData.first_name ?? null,
  last_name: apiData.last_name ?? null,
  username: apiData.username ?? null,
  level_cap: apiData.level_cap ?? null,
  next_gate_price: apiData.next_gate_price ?? null,
  level_capped: apiData.level_capped ?? false,
  exp_booster_until: apiData.exp_booster_until ?? null,
  exp_booster_multiplier: apiData.exp_booster_multiplier ?? 1,
  balance_ton: apiData.balance_ton ?? 0,
  balance_usdt: apiData.balance_usdt ?? 0,
  currency_multiplier: apiData.currency_multiplier ?? 1,
  is_mayor: apiData.is_mayor ?? false,
  notifications_warmup_granted: apiData.notifications_warmup_granted ?? false,
  comeback_offer: apiData.comeback_offer ?? false,
  comeback_vip_days: apiData.comeback_vip_days ?? 0,
});

export const getUserData = async (): Promise<UserData> => {
  const { data } = await getUserPublicCalculateUserGet({ throwOnError: true });
  return normalizeUserData(data);
};

/**
 * Викликає calculate-user при закритті додатку.
 * Свідомий спец-випадок unload-beacon: сирий fetch з keepalive — згенерований клієнт не підтримує keepalive.
 */
export const getUserDataOnUnload = (): void => {
  try {
    fetch(`${getApiBase()}/calculate-user`, {
      method: "GET",
      keepalive: true,
      headers: getCommonHeaders(),
    }).catch(() => {
      // Ігноруємо помилки при закритті
    });
  } catch {
    // Ігноруємо помилки при закритті
  }
};
